import { ListResponse, ObjectResponse } from '@/src/types/common';
import request from '../axios';
import { plainToInstance } from 'class-transformer';
import {
  PartnershipCompainesDto,
  PartnershipLinksDto,
} from '@/src/models/PartnershipCompainesDto';
import {
  GetPartnershipCompainesRequestParams,
  GetPartnershipCompaniesByIdRequestParams,
  GetPartnershipLinksByIdRequestParams,
  GetPartnershipLinksRequestParams,
  PostPartnershipCompaniesRequestBody,
  PostPartnershipLinksRequestBody,
  PutPartnershipCompaniesRequestBody,
  PutPartnershipLinksRequestBody,
  DeletePartnershipLinksByIdRequestBody,
  GetRecyclingSummaryRequestParams,
  GetCustomerByPartnerLinkIdRequestParams,
} from './partnershipCompaines.dtos';
import { PartnershipRecyclingSummaryDto } from '@/src/models/PartnershipRecyclingSummaryDto';
const URL = {
  partnershipCompanies: '/admin/partnership-companies',
  partnershipCompaniesDetail: '/admin/partnership-companies/{id}',
  partnershipLinks: '/admin/partnership-links',
  partnershipLinksDetail: '/admin/partnership-links/{id}',
  partnershipCustomersExcel: '/admin/customers/excel/export',
  partnershipParticipantsRecyclingSummary:
    '/admin/partnership-participants/recycling-summary/{id}',
  partnershipParticipantsRecyclingSummaryExcel:
    '/admin/partnership-participants/recycling-summary/excel-export',
  partnershipLinkCustomers: '/admin/partnership-participants/customers',
  partnershipLinkCustomersExcel:
    '/admin/partnership-participants/customers/excel-export',
};

export const getPartnershipCompaniesAPI = async (
  requestParams: Partial<GetPartnershipCompainesRequestParams>,
) => {
  const { page = 1, pageSize = 10, ...rest } = requestParams;

  const { data } = await request<ListResponse<PartnershipCompainesDto>>({
    method: 'get',
    url: URL.partnershipCompanies,
    queryParams: { ...rest, page, pageSize },
  });

  const rows = plainToInstance(PartnershipCompainesDto, data.rows);

  return { rows, count: data.count };
};

export const getPartnershipCompainesByIdAPI = async ({
  id,
}: Partial<GetPartnershipCompaniesByIdRequestParams>) => {
  const { data } = await request<ObjectResponse<PartnershipCompainesDto>>({
    method: 'get',
    url: URL.partnershipCompaniesDetail.replace('{id}', String(id)),
  });

  return plainToInstance(PartnershipCompainesDto, data.row);
};

export const putPartnershipCompaniesAPI = async (
  requestBody: PutPartnershipCompaniesRequestBody,
) => {
  const { id, ...rest } = requestBody;

  const { data } = await request<ObjectResponse<PartnershipCompainesDto>>({
    method: 'put',
    url: URL.partnershipCompaniesDetail.replace('{id}', String(id)),
    requestBody: rest,
  });

  return plainToInstance(PartnershipCompainesDto, data.row);
};

export const postPartnershipCompaniesAPI = async (
  requestBody: PostPartnershipCompaniesRequestBody,
) => {
  const { data } = await request<ObjectResponse<PartnershipCompainesDto>>({
    method: 'post',
    url: URL.partnershipCompanies,
    requestBody,
  });

  return plainToInstance(PartnershipCompainesDto, data.row);
};

export const getPartnershipLinksAPI = async (
  requestParams: Partial<GetPartnershipLinksRequestParams>,
) => {
  const { page = 1, pageSize = 10, ...rest } = requestParams;

  const { data } = await request<ListResponse<PartnershipLinksDto>>({
    method: 'get',
    url: URL.partnershipLinks,
    queryParams: { ...rest, page, pageSize },
  });

  const rows = plainToInstance(PartnershipLinksDto, data.rows);

  return { rows, count: data.count };
};

export const getPartnershipLinksByIdAPI = async ({
  id,
}: Partial<GetPartnershipLinksByIdRequestParams>) => {
  const { data } = await request<ObjectResponse<PartnershipLinksDto>>({
    method: 'get',
    url: URL.partnershipLinksDetail.replace('{id}', String(id)),
  });

  return plainToInstance(PartnershipLinksDto, data.row);
};

export const putPartnershipLinksAPI = async (
  requestBody: PutPartnershipLinksRequestBody,
) => {
  const { id, ...rest } = requestBody;

  const { data } = await request<ObjectResponse<PartnershipLinksDto>>({
    method: 'put',
    url: URL.partnershipLinksDetail.replace('{id}', String(id)),
    requestBody: rest,
  });

  return plainToInstance(PartnershipLinksDto, data.row);
};

export const postPartnershipLinksAPI = async (
  requestBody: PostPartnershipLinksRequestBody,
) => {
  const { data } = await request<ObjectResponse<PartnershipLinksDto>>({
    method: 'post',
    url: URL.partnershipLinks,
    requestBody,
  });

  return plainToInstance(PartnershipLinksDto, data.row);
};

export const deletePartnershipLinksByIdAPI = async ({
  id,
}: DeletePartnershipLinksByIdRequestBody) => {
  const { data } = await request<ObjectResponse<PartnershipLinksDto>>({
    method: 'delete',
    url: URL.partnershipLinksDetail.replace('{id}', String(id)),
  });

  return plainToInstance(PartnershipLinksDto, data.row);
};

export const getPartnershipCustomersExcelAPI = async (
  password: string,
  requestParams: Partial<GetPartnershipLinksRequestParams>,
) => {
  const { data } = await request<Blob>({
    method: 'get',
    url: URL.partnershipCustomersExcel,
    queryParams: { password, ...requestParams },
    config: { responseType: 'blob' },
  });

  return data;
};

// 파트너사 링크별 수거신청누적정보 조회
export const getRecyclingSummaryAPI = async (
  params: GetRecyclingSummaryRequestParams,
) => {
  const { id } = params;

  const { data } = await request<
    ObjectResponse<PartnershipRecyclingSummaryDto>
  >({
    method: 'get',
    url: URL.partnershipParticipantsRecyclingSummary.replace(
      '{id}',
      String(id),
    ),
  });

  return data.row;
};

// 파트너사 링크별 수거신청 누적 정보 엑셀 다운로드
export const getRecyclingSummaryExcelAPI = async (
  password: string,
  requestParams: Partial<GetRecyclingSummaryRequestParams>,
) => {
  const { data } = await request<Blob>({
    method: 'get',
    url: URL.partnershipParticipantsRecyclingSummaryExcel,
    queryParams: { ...requestParams, password },
    config: { responseType: 'blob' },
  });

  return data;
};

// 파트너사 가입 회원 조회
export const getCustomerByPartnerLinkIdAPI = async (
  queryParams: GetCustomerByPartnerLinkIdRequestParams,
) => {
  const { data } = await request<ListResponse<any>>({
    method: 'get',
    url: URL.partnershipLinkCustomers,
    queryParams,
  });

  return data;
};

// 파트너사 가입 회원 엑셀 다운로드
export const getCustomerByPartnerLinkIdExcelAPI = async (
  password: string,
  requestParams: Partial<GetCustomerByPartnerLinkIdRequestParams>,
) => {
  const { data } = await request<Blob>({
    method: 'get',
    url: URL.partnershipLinkCustomersExcel,
    queryParams: { ...requestParams, password },
    config: { responseType: 'blob' },
  });

  return data;
};
